import DashboardApi from '../../api/DashboardApi.js';
import { DASHBOARD_ACTIONS } from '../../reducers/dashboard.js';
import { API_BASE_URL, STATIC_SPACE_URL } from '../../api/constants.js';
import { formatAgendaEvents } from '../../utils/agendaFormatter.js';
import { toPickerValue } from '../user-profile/documentDates.js';

// ============================================================================
// USER MENU ACTIONS (for breadcrumbs)
// ============================================================================

// Fetch user menu for current organization
export const fetchUserMenu = (orgUnique) => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_USER_MENU_REQUEST });

  try {
    const data = await DashboardApi.getUserMenu(orgUnique);

    // Parse the data to get a clean object (fixes Proxy/getter issues)
    const cleanData = JSON.parse(JSON.stringify(data));

    // Try both locations - backend returns organizationMenu, frontend uses userMenu
    const userMenu = cleanData.userMenu || cleanData.data?.userMenu || cleanData.organizationMenu || cleanData.data?.organizationMenu || [];

    // Extract organizationSettings from API response
    const organizationSettings = cleanData.organizationSettings || cleanData.data?.organizationSettings || null;

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_USER_MENU_SUCCESS,
      payload: {
        userMenu: userMenu,
        organizationSettings: organizationSettings
      }
    });

    return userMenu;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_USER_MENU_FAILURE,
      payload: { error: error.message }
    });
    throw error;
  }
};

// Fetch full organization dashboard data
export const fetchOrganizationDashboard = (orgUnique) => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_USER_MENU_REQUEST });

  try {
    const response = await DashboardApi.getOrganizationDashboard(orgUnique);
    const userMenu = response.data?.userMenu || [];

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_USER_MENU_SUCCESS,
      payload: {
        userMenu
      }
    });

    return response;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_USER_MENU_FAILURE,
      payload: { error: error.message }
    });
    throw error;
  }
};

// ============================================================================
// USER DASHBOARD ACTIONS (for user dashboard page)
// ============================================================================

// Fetch all dashboard data
export const fetchDashboardData = () => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_REQUEST });

  try {
    // Fetch data from API - run all requests in parallel for better performance
    let timezonesResponse = null;

    // Fetch all required data - core requests
    const [notificationsResponse, agendaResponse, organizationsResponse, profileResponse] = await Promise.all([
      DashboardApi.getUserNotifications(),
      DashboardApi.getUserAgenda(),
      DashboardApi.getUserOrganizations(false), // Explicitly set activeOnly=false
      DashboardApi.getUserProfile() // Fetch user profile
    ]);

    // Fetch timezones using direct apiRequest to avoid caching issues
    try {
      timezonesResponse = await DashboardApi.apiRequest('/calendar/timezones', { method: 'GET' });
    } catch (error) {
      // Failed to fetch timezones - will use user timezone as fallback
    }

    // Initialize formattedAgenda as an empty array by default
    let formattedAgenda = [];

    // Initialize formattedOrganizations as an empty array by default
    let formattedOrganizations = [];

    // Process notifications data
    let formattedNotifications = [];

    // Check different possible API response structures
    const notificationsArray = notificationsResponse && notificationsResponse.data
      ? (notificationsResponse.data.notifications || (notificationsResponse.data.data && notificationsResponse.data.data.notifications) || [])
      : [];

    if (notificationsArray.length > 0) {
      formattedNotifications = notificationsArray.map((notification, index) => ({
        id: notification.id || index + 1,
        message: notification.text,
        color: notification.status ? 'success' : 'warning',
        read: !!notification.status,
        type: notification.type || 'general'
      }));
    }

    // Get user timezone from profile (if available)
    let userProfileTimeZone = profileResponse?.data?.data?.timeZone;

    // API can return timezone as object {id: "America/New_York"} or as string
    if (userProfileTimeZone && typeof userProfileTimeZone === 'object' && userProfileTimeZone.id) {
      userProfileTimeZone = userProfileTimeZone.id;
    }

    // Create map location_id -> timezone from API timezones
    const timezonesMap = {};
    if (timezonesResponse?.data?.data?.timezones) {
      timezonesResponse.data.data.timezones.forEach(tz => {
        if (tz.location_id && tz.timezone) {
          // Store the whole timezone object so we can access tz.timezone.id
          timezonesMap[tz.location_id] = tz.timezone;
        }
      });
    }

    // Format agenda events using shared formatter
    if (agendaResponse?.data?.data) {
      formattedAgenda = formatAgendaEvents(agendaResponse.data, {
        userTimeZone: userProfileTimeZone,
        timezonesMap: timezonesMap
      });
    }

    // Process user profile data
    let formattedProfileData = null;

    if (profileResponse && profileResponse.data && profileResponse.data.data) {
      const profileData = profileResponse.data.data;

      // Format profile data in required format
      formattedProfileData = {
        name: `${profileData.user.firstName} ${profileData.user.lastName}`,
        email: profileData.user.email,
        // Filter out empty strings and null/undefined values
        phone: (profileData.user.mobilePhoneNumber || profileData.user.homePhoneNumber || profileData.user.workPhoneNumber || '').trim() || null,
        avatar: profileData.user.smallImage ? `${STATIC_SPACE_URL}/${profileData.user.smallImage}` : null,
        timeZone: profileData.timeZone,

        // Process documents
        documents: profileData.documents ? profileData.documents.map(doc => ({
          id: doc.id,
          type: doc.type,
          number: doc.number,
          name: doc.name
        })) : [],

        // Process certificates
        certificates: profileData.certificates ? profileData.certificates.map(cert => ({
          type: cert.type,
          value: cert.grade || 'Valid'
        })) : [],

        // Process endorsements
        endorsements: profileData.endorsements ? profileData.endorsements.map(end => end.name) : [],

        // Process flight reviews
        flightReviews: profileData.flyReviews ? profileData.flyReviews.map(review => ({
          id: review.id,
          type: review.type === 'faa' ? 'FAA Flight Review' : `${review.organizationName || 'Organization'} Review`,
          date: toPickerValue(review.date)?.toLocaleDateString() ?? 'N/A',
          expirationDate: toPickerValue(review.expirationDate)?.toLocaleDateString() ?? 'N/A'
        })) : []
      };
    }

    // Process organizations data
    if (organizationsResponse && organizationsResponse.data && organizationsResponse.data.data && organizationsResponse.data.data.organizations) {
      const orgsData = organizationsResponse.data.data.organizations;

      // Filter out organizations where user has 'Inactive' role
      const activeOrgsData = orgsData.filter(org => {
        if (!org.roles || org.roles.length === 0) return true;
        // Check if any role is 'Inactive' (case-insensitive)
        const hasInactiveRole = org.roles.some(role =>
          typeof role === 'string'
            ? role.toLowerCase() === 'inactive'
            : role.name && role.name.toLowerCase() === 'inactive'
        );
        return !hasInactiveRole;
      });

      // Convert data to required format
      formattedOrganizations = activeOrgsData.map(org => {
        const roleString = org.roles && org.roles.length > 0 ? org.roles.join(', ') : 'Member';
        const isOwner = roleString.includes('Owner');
        const isOverdue = org.status === 'overdue';

        // Determine href based on status and role:
        // - If not overdue → org dashboard
        // - If overdue AND Owner → /payments/
        // - If overdue AND NOT Owner → null (not clickable)
        let href = null;
        if (!isOverdue) {
          href = `/org/${org.unique}/organizations/dashboard`;
        } else if (isOwner) {
          href = '/payments/';
        }
        // else: overdue + not owner → href stays null

        return {
          id: org.id,
          name: org.name,
          unique: org.unique,
          role: roleString,
          status: org.status || 'inactive',
          href,
          // Use static base URL for organization logos
          // Example: https://dev.holdshort.com/storage/organization/js_logo_70ccebb20351d7611f46d80664593cc4-md.jpeg
          image: org.image ? `${STATIC_SPACE_URL}/${org.image}` : null,
          // Request count for join requests (only populated for Owner/Manager roles)
          requestCount: org.requestCount || 0,
          // Trial period flag
          trialWithoutCard: org.trialWithoutCard,
          // Privacy status flag
          private: org.private || false
        };
      });

      // Sort organizations: active first, then inactive
      formattedOrganizations.sort((a, b) => {
        // First sort by status (active first)
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;

        // If status is the same, sort by name
        return a.name.localeCompare(b.name);
      });
    }

    // Process invitations data (from profile response)
    let formattedInvitations = [];
    if (profileResponse?.data?.data?.invitations) {
      formattedInvitations = profileResponse.data.data.invitations.map(inv => ({
        id: inv.id,
        organizationName: inv.organizationName,
        organizationUnique: inv.organizationUnique,
        date: inv.date,
        hash: inv.hash
      }));
    }

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_SUCCESS,
      payload: {
        profile: formattedProfileData,
        organizations: formattedOrganizations,
        agenda: formattedAgenda,
        notifications: formattedNotifications,
        invitations: formattedInvitations
      }
    });

    return { success: true, data: { agenda: formattedAgenda } };
  } catch (error) {
    console.error('Failed to fetch dashboard data:', error);

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Fetch user profile
export const fetchUserProfile = () => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_REQUEST });

  try {
    const response = await DashboardApi.getUserProfile();

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_SUCCESS,
      payload: {
        profile: response.data
      }
    });

    return response;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Fetch user organizations
export const fetchUserOrganizations = () => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_REQUEST });

  try {
    const response = await DashboardApi.getUserOrganizations();

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_SUCCESS,
      payload: {
        organizations: response.data
      }
    });

    return response;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Fetch user agenda
export const fetchUserAgenda = (limit = 10) => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_REQUEST });

  try {
    const response = await DashboardApi.getUserAgenda(limit);

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_SUCCESS,
      payload: {
        agenda: response.data
      }
    });

    return response;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Fetch user notifications
export const fetchUserNotifications = () => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_REQUEST });

  try {
    const response = await DashboardApi.getUserNotifications();

    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_SUCCESS,
      payload: {
        notifications: response.data
      }
    });

    return response;
  } catch (error) {
    dispatch({
      type: DASHBOARD_ACTIONS.FETCH_DASHBOARD_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Mark notification as read
export const markNotificationAsRead = (notificationId) => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.MARK_NOTIFICATION_READ_REQUEST });

  try {
    const response = await DashboardApi.markNotificationAsRead(notificationId);

    dispatch({
      type: DASHBOARD_ACTIONS.MARK_NOTIFICATION_READ_SUCCESS,
      payload: { notificationId }
    });

    return response;
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    dispatch({
      type: DASHBOARD_ACTIONS.MARK_NOTIFICATION_READ_FAILURE,
      payload: { error: error.message }
    });

    throw error;
  }
};

// Reject invitation from organization
// TODO: Replace with API call when backend implements POST /user/reject-invitation/{orgUnique}
export const rejectInvitation = (organizationUnique) => async (dispatch) => {
  dispatch({ type: DASHBOARD_ACTIONS.REJECT_INVITATION_REQUEST });

  try {
    // Call legacy PHP route via fetch
    // redirect: 'manual' prevents following the redirect (which causes mixed content error on HTTPS)
    await fetch(`/users/rejectInvitation/${organizationUnique}/`, {
      method: 'GET',
      credentials: 'include',
      redirect: 'manual'
    });

    // Update Redux state to remove the invitation from UI
    dispatch({
      type: DASHBOARD_ACTIONS.REJECT_INVITATION_SUCCESS,
      payload: { organizationUnique }
    });
  } catch (error) {
    console.error('Failed to reject invitation:', error);
    dispatch({
      type: DASHBOARD_ACTIONS.REJECT_INVITATION_FAILURE,
      payload: { error: error.message }
    });
  }
};
